"use client";

import { LayoutGrid, Check, Settings, Monitor, Database, Target } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useView, ViewMode } from "@/contexts/view-context";
import { useLanguage } from "@/hooks/useLanguage";
import { translations } from "@/lib/translations";
import { ViewSearchModal } from "./ViewSearchModal";
import { TokenBrief } from "@/components/shared/TokenBrief";
import { toast } from "sonner";
import { FeatureGuideHint } from "@/components/guides/feature-guide-hint";
import {
	useFeatureGuide,
	useFeatureGuideCandidate,
} from "@/contexts/feature-guide-context";

export function VersionToggle() {
	const { viewMode, setViewMode, focusTokenId, setFocusTokenId } = useView();
	const { language } = useLanguage();
	const t = (translations[language] as any).view;
	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const { isGuideActive, dismissGuide } = useFeatureGuide();
	const isGuideVisible = isGuideActive("view-mode-toggle");

	useFeatureGuideCandidate("view-mode-toggle", true);

	const modes = [
		{
			id: "standard",
			label: t.standard,
			desc: t.standardDesc,
			icon: <Monitor className="h-4 w-4" />,
			color: "text-blue-500",
		},
		{
			id: "pro",
			label: t.pro,
			desc: t.proDesc,
			icon: <Database className="h-4 w-4" />,
			color: "text-cyan-500",
		},
		{
			id: "focus",
			label: t.focus,
			desc: t.focusDesc,
			icon: <Target className="h-4 w-4" />,
			color: "text-light-primary",
		},
	];

	const handleModeChange = (modeId: string) => {
		setViewMode(modeId as ViewMode);
		toast.success(t.switchSuccess.replace("{mode}", modes.find(m => m.id === modeId)?.label));
	};

	const handleTokenSelect = (tokenId: string) => {
		setFocusTokenId(tokenId);
		setViewMode("focus");
		setIsSearchOpen(false);
		toast.success(t.switchSuccess.replace("{mode}", t.focus) + `: ${tokenId}`);
	};

	return (
		<div className="relative">
			<DropdownMenu onOpenChange={(open) => {
				if (open && isGuideVisible) dismissGuide("view-mode-toggle");
			}}>
				<DropdownMenuTrigger asChild>
					<Button
						variant="outline"
						size="icon"
						className={`relative text-light-text dark:text-dark-text border-light-border dark:border-dark-border hover:bg-light-surface dark:hover:bg-dark-surface transition-all duration-300 ${
							isGuideVisible ? "ring-2 ring-light-primary animate-pulse" : ""
						}`}
					>
						<LayoutGrid className="h-[1.2rem] w-[1.2rem] transition-all" />
						<span className="absolute -top-1 -right-1 bg-light-primary dark:bg-dark-primary text-white text-xs px-1 py-0.5 rounded-full text-[8px] font-bold uppercase">
							{viewMode === "standard" ? "COM" : viewMode === "pro" ? "PRO" : "FOC"}
						</span>
						<span className="sr-only">{t.title}</span>
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-64">
					<DropdownMenuLabel>{t.title}</DropdownMenuLabel>
					<DropdownMenuSeparator />
					{modes.map((mode) => (
						<DropdownMenuItem
							key={mode.id}
							className="flex flex-col items-start gap-1 py-2 cursor-pointer"
							onClick={() => handleModeChange(mode.id)}
						>
							<div className="flex items-center justify-between w-full">
								<div className="flex items-center gap-2">
									<div className={mode.color}>{mode.icon}</div>
									<span className="font-medium">{mode.label}</span>
								</div>
								{viewMode === mode.id && (
									<Check className="h-4 w-4 text-light-primary" />
								)}
							</div>
							<p className="text-xs text-muted-foreground">{mode.desc}</p>
							{mode.id === "focus" && (
								<div className="flex items-center justify-between w-full mt-1 pt-1 border-t border-light-border/50 dark:border-dark-border/50">
									<div className="flex-1 min-w-0 mr-2">
										<TokenBrief tokenId={focusTokenId} textClassName="text-[10px]" />
									</div>
									<Button
										variant="ghost"
										size="sm"
										className="h-6 px-2 text-[10px] gap-1 hover:bg-light-primary/10 shrink-0"
										onClick={(e) => {
											e.stopPropagation();
											setIsSearchOpen(true);
										}}
									>
										<Settings className="h-3 w-3" />
										{t.setting}
									</Button>
								</div>
							)}
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>

			<FeatureGuideHint
				guideId="view-mode-toggle"
				message={t.guideHint}
				actionLabel={t.guideAction}
				className="absolute top-full right-0 mt-3 w-52 z-[60]"
			/>

			<ViewSearchModal
				isOpen={isSearchOpen}
				onClose={() => setIsSearchOpen(false)}
				onSelect={handleTokenSelect}
			/>
		</div>
	);
}
